import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI, projectAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import { toast } from 'sonner';
import './Team.css';

const Team = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);

  const user = useAuthStore((state) => state.user);



  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll();
      let allUsers = response.data.data || [];

      // Filter for Project Lead - only show team members from supervised projects
      if (user?.role === 'Project Lead') {
        const projectsRes = await projectAPI.getAll();
        const allProjects = projectsRes.data.data || [];

        // Get unique team member IDs from projects (backend already filters projects for Project Lead)
        const teamMemberIds = new Set();
        allProjects.forEach(project => {
          if (project.teamMembers && Array.isArray(project.teamMembers)) {
            project.teamMembers.forEach(member => {
              const memberId = member._id || member;
              if (memberId) teamMemberIds.add(memberId.toString());
            });
          }
        });

        // Filter users to only show team members
        allUsers = allUsers.filter(u => teamMemberIds.has(u._id.toString()));
      }

      setUsers(allUsers);
    } catch (error) {
      console.error('Failed to load team:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };













  const handleDownloadTemplate = async () => {
    try {
      const response = await userAPI.downloadTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'team_members_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setImportErrors([]);
    }
  };

  const handleBulkImport = async () => {
    if (!importFile) {
      toast.warning('Please select a file first');
      return;
    }

    setImporting(true);
    setImportErrors([]);

    try {
      const response = await userAPI.bulkImport(importFile);
      toast.success(response.data.message);
      setShowBulkImportModal(false);
      setImportFile(null);
      loadTeam();
    } catch (error) {
      if (error.response?.data?.errors) {
        setImportErrors(error.response.data.errors);
      } else {
        toast.error(error.response?.data?.message || 'Failed to import team members');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleView = (memberId) => {
    navigate(`/team/view/${memberId}`);
    setOpenMenuId(null);
  };

  const handleEdit = (memberId) => {
    navigate(`/team/edit/${memberId}`);
    setOpenMenuId(null);
  };

  const handleDelete = async (memberId) => {
    if (window.confirm('Are you sure you want to deactivate this team member? they can be re-activated later.')) {
      try {
        await userAPI.deleteUser(memberId);
        toast.success('Team member deactivated successfully');
        loadTeam();
        setOpenMenuId(null);
      } catch (error) {
        toast.error('Failed to deactivate team member: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handlePermanentDelete = async (memberId) => {
    if (window.confirm('WARNING: Are you sure you want to PERMANENTLY delete this team member? This action cannot be undone.')) {
      try {
        await userAPI.permanentlyDelete(memberId);
        toast.success('Team member permanently deleted');
        loadTeam();
        setOpenMenuId(null);
      } catch (error) {
        toast.error('Failed to permanently delete team member: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleResetPassword = async (memberId) => {
    const newPassword = prompt('Enter new password:');
    if (newPassword) {
      try {
        await userAPI.resetPassword(memberId, { password: newPassword });
        toast.success('Password reset successfully');
        setOpenMenuId(null);
      } catch (error) {
        toast.error('Failed to reset password: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const canManageTeam = user?.role === 'HR' || user?.role === 'Operations Head';

  return (
    <Layout>
      <div className="team-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Team</h1>
            <p className="page-subtitle">
              {user?.role === 'Project Lead'
                ? 'View your team members from supervised projects'
                : 'Manage team members and reporting structure'}
            </p>
          </div>
          {canManageTeam && (
            <div className="header-actions">
              <button className="btn-secondary" onClick={() => setShowBulkImportModal(true)}>
                Bulk Import
              </button>
              <button className="btn-create" onClick={() => navigate('/team/add')}>
                Add Team Member
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading team...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <h3>No Team Members Found</h3>
            <p>There are no team members in your organization yet.</p>
            {canManageTeam && (
              <button className="btn-create" onClick={() => navigate('/team/add')}>
                Add First Team Member
              </button>
            )}
          </div>
        ) : (
          <div className="team-list">
            <table className="team-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Designation</th>
                  <th>Mobile</th>
                  <th>Department</th>
                  <th>Reports To</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map(member => (
                  <tr key={member._id} className="team-row">
                    <td className="cell-name">
                      <div className="name-with-avatar">
                        <Avatar
                          name={member.name}
                          profileImage={member.profileImage}
                          size="small"
                        />
                        <span>{member.name}</span>
                      </div>
                    </td>
                    <td>{member.email}</td>
                    <td>
                      <span className="role-badge">{member.role}</span>
                    </td>
                    <td>{member.designation || '-'}</td>
                    <td>{member.mobile || '-'}</td>
                    <td>{member.department || '-'}</td>
                    <td>{member.reportsTo?.name || '-'}</td>
                    <td>
                      <span className={`status-badge ${member.isActive ? 'active' : 'inactive'}`}>
                        {member.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-menu">
                        <button
                          className="btn-menu"
                          onClick={() => setOpenMenuId(openMenuId === member._id ? null : member._id)}
                        >
                          ⋮
                        </button>
                        {openMenuId === member._id && (
                          <div className="dropdown-menu">
                            <button
                              className="menu-item view"
                              onClick={() => handleView(member._id)}
                            >
                              <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              View
                            </button>
                            {canManageTeam && (
                              <>
                                <button
                                  className="menu-item edit"
                                  onClick={() => handleEdit(member._id)}
                                >
                                  <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                  Edit
                                </button>
                                <button
                                  className="menu-item reset"
                                  onClick={() => handleResetPassword(member._id)}
                                >
                                  <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="1" />
                                    <path d="M12 1v6m0 6v6" />
                                    <path d="M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24" />
                                    <path d="M1 12h6m6 0h6" />
                                    <path d="M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24" />
                                  </svg>
                                  Reset Password
                                </button>
                                <button
                                  className="menu-item delete"
                                  onClick={() => handleDelete(member._id)}
                                >
                                  <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                                    <line x1="12" y1="2" x2="12" y2="12" />
                                  </svg>
                                  Deactivate
                                </button>
                                <button
                                  className="menu-item delete"
                                  onClick={() => handlePermanentDelete(member._id)}
                                  style={{ color: '#ef4444' }}
                                >
                                  <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    <line x1="10" y1="11" x2="10" y2="17" />
                                    <line x1="14" y1="11" x2="14" y2="17" />
                                  </svg>
                                  Permanent Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}





        {showBulkImportModal && (
          <div className="modal-overlay" onClick={() => setShowBulkImportModal(false)}>
            <div className="modal-content bulk-import-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Bulk Import Team Members</h2>
                <button className="btn-close" onClick={() => setShowBulkImportModal(false)}>
                  ×
                </button>
              </div>

              <div className="bulk-import-content">
                <div className="import-step">
                  <h3>Step 1: Download Template</h3>
                  <p>Download the Excel template with the required format:</p>
                  <button className="btn-template" onClick={handleDownloadTemplate}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', marginRight: '8px' }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="12" y1="18" x2="12" y2="12" />
                      <line x1="9" y1="15" x2="15" y2="15" />
                    </svg>
                    Download Template
                  </button>
                </div>

                <div className="import-step">
                  <h3>Step 2: Fill Template</h3>
                  <p>Fill the template with team member details. Required fields are marked with *:</p>
                  <div className="field-categories">
                    <div className="field-category">
                      <h4>Basic Information</h4>
                      <ul>
                        <li>Full Name *</li>
                        <li>Username *</li>
                        <li>Email * (must be unique)</li>
                        <li>Password * (min 6 chars, uppercase, lowercase, numbers)</li>
                        <li>Mobile *</li>
                        <li>Date of Birth</li>
                        <li>Gender (Male/Female/Other)</li>
                        <li>Marital Status</li>
                        <li>Nationality</li>
                      </ul>
                    </div>
                    <div className="field-category">
                      <h4>Address Details</h4>
                      <ul>
                        <li>Address</li>
                        <li>City</li>
                        <li>State</li>
                        <li>Zip Code</li>
                        <li>Country</li>
                      </ul>
                    </div>
                    <div className="field-category">
                      <h4>Emergency Contact</h4>
                      <ul>
                        <li>Emergency Contact Name</li>
                        <li>Emergency Contact Phone</li>
                        <li>Emergency Contact Relation</li>
                      </ul>
                    </div>
                    <div className="field-category">
                      <h4>Employment Details</h4>
                      <ul>
                        <li>Role * (HR, Developer, QA Engineer, etc.)</li>
                        <li>Department</li>
                        <li>Employee ID</li>
                        <li>Designation</li>
                        <li>Joining Date</li>
                        <li>Employment Type</li>
                        <li>Work Location</li>
                        <li>Annual Salary</li>
                        <li>Reporting Manager (email)</li>
                      </ul>
                    </div>
                    <div className="field-category">
                      <h4>Education & Experience</h4>
                      <ul>
                        <li>Education</li>
                        <li>University</li>
                        <li>Graduation Year</li>
                        <li>Years of Experience</li>
                        <li>Previous Company</li>
                        <li>Skills</li>
                      </ul>
                    </div>
                    <div className="field-category">
                      <h4>Status</h4>
                      <ul>
                        <li>Status (Active/Inactive)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="import-step">
                  <h3>Step 3: Upload File</h3>
                  <div className="file-upload">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="file-input"
                      id="bulk-import-file"
                    />
                    <label htmlFor="bulk-import-file" className="file-label">
                      {importFile ? importFile.name : 'Choose Excel File'}
                    </label>
                  </div>
                </div>

                {importErrors.length > 0 && (
                  <div className="import-errors">
                    <h4>Validation Errors:</h4>
                    {importErrors.map((error, index) => (
                      <div key={index} className="error-row">
                        <strong>Row {error.row}:</strong>
                        <ul>
                          {error.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn-cancel"
                  onClick={() => setShowBulkImportModal(false)}
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  className="btn-submit"
                  onClick={handleBulkImport}
                  disabled={!importFile || importing}
                >
                  {importing ? 'Importing...' : 'Import Team Members'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default Team;
